import { useState, useEffect } from 'react'
import { Input } from "../../ui/input"
import { Button } from "../../ui/button"
import { Send, ArrowRight, Play, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'

export function Hero() {
  const navigate = useNavigate()
  const { isSignedIn } = useAuth()
  const reelTerms = [
    'Marketing Scripts',
    'Action plan',
    'PRD',
    'Summary sessions',
    'Follow-up emails'
  ]
  const [currentTermIndex, setCurrentTermIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [isDemoOpen, setIsDemoOpen] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true)
      setTimeout(() => {
        setCurrentTermIndex((prev) => (prev + 1) % reelTerms.length)
        setIsAnimating(false)
      }, 150)
    }, 1500)
    return () => clearInterval(interval)
  }, [reelTerms.length])

  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const handleSend = () => {
    const prompt = inputValue.trim()
    if (!prompt) return
    if (isSignedIn) {
      navigate('/chat', { state: { initialPrompt: prompt } })
    } else {
      // On signed-out, keep modal; do not persist message
    }
  }

  return (
    <section className="relative min-h-[min(84vh,680px)]  flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700">
      <div className="absolute inset-0 opacity-40 dark:opacity-20">
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, #e2e8f0 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-gradient-to-br from-indigo-500/12 via-blue-500/10 to-transparent dark:from-indigo-400/16 dark:via-blue-400/14 dark:to-transparent rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden pointer-events-none">
        <div className="absolute bottom-10 right-20 w-2 h-2 bg-blue-400/20 rounded-full animate-pulse"></div>
        <div className="absolute bottom-32 right-10 w-1 h-1 bg-indigo-400/30 rounded-full animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 right-40 w-1.5 h-1.5 bg-cyan-400/25 rounded-full animate-pulse delay-500"></div>
      </div>

      <div className="container mx-auto max-w-[1100px] px-10 md:px-[72px] relative z-10  mt-10 md:mt-20 ">
        <div className="flex flex-col items-center text-center space-y-5  ">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-slate-900 dark:text-slate-100 max-w-[42ch] animate-in fade-in duration-320 text-[48px] mt-[0px] mr-[0px] mb-[24px] ml-[0px]">
            <span 
              className="relative inline-flex items-center"
              aria-live="polite"
            >
              <span
                className={`inline-flex items-center rounded-md px-3 py-1 backdrop-blur-md bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-purple-500/20 ring-1 ring-white/10 dark:ring-white/10 transition-all duration-150 ease-[cubic-bezier(.2,.8,.2,1)] text-[0.85em] sm:text-[0.9em] ${
                  isAnimating && !prefersReducedMotion 
                    ? 'transform translate-y-2 opacity-0' 
                    : 'transform translate-y-0 opacity-100'
                } ${
                  prefersReducedMotion && isAnimating ? 'opacity-50' : ''
                }`}
              >
                {reelTerms[currentTermIndex]}
              </span>
            </span>
            <br />
            From draft to delivery — AI at your service. 
          </h1>

          <div className="animate-in fade-in duration-320 delay-75">
            <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 max-w-[58ch] font-medium">
              QL's Artificial Intelligence Routine Assistant
            </p>
          </div>

          <div className="w-full max-w-2xl relative animate-in fade-in duration-240 delay-100">
            {isSignedIn ? (
              // Input box for signed-in users
              <div className="relative h-14 rounded-2xl bg-white dark:bg-white/95 shadow-sm dark:shadow-slate-900/20 border border-slate-200/50 dark:border-slate-300/20 hover:shadow-md hover:border-slate-300/60 dark:hover:border-slate-300/30 transition-all duration-200 group">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && inputValue.trim()) {
                      handleSend();
                    }
                  }}
                  placeholder="How can I help you?"
                  className="h-full pl-4 pr-14 bg-transparent border-0 focus-visible:ring-0 text-slate-900 dark:text-slate-800 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                />
                <button 
                  onClick={handleSend}
                  className="absolute right-2 top-2 h-10 w-10 rounded-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white hover:scale-[1.05] transition-all duration-200 flex items-center justify-center group/send disabled:opacity-50 disabled:cursor-not-allowed z-10"
                  disabled={!inputValue.trim()}
                >
                  <Send className="h-4 w-4 group-hover/send:scale-110 transition-transform duration-150" />
                </button>
              </div>
            ) : (
              // Start with AIRA button for signed-out users
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
                <Button
                  onClick={() => navigate('/login')}
                  className="h-12 w-[180px] rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white transition-all duration-180 hover:scale-[1.02] focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 relative group/btn font-medium whitespace-nowrap"
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-180 blur-lg -z-10"></div>
                  Start with AIRA
                  <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform duration-200" />
                </Button>
                <Button
                  variant="outline"
                  className="h-12 w-[180px] rounded-2xl border-2 border-indigo-500/50 dark:border-indigo-400/50 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all duration-180 hover:scale-[1.02] focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900 font-medium whitespace-nowrap"
                  onClick={() => setIsDemoOpen(true)}
                >
                  <Play className="mr-2 h-4 w-4" />
                  Watch Demo
                </Button>
              </div>
            )}
          </div>

          <div className="pt-4 animate-in fade-in duration-320 delay-150">
            <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
              <span className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors duration-200">Fast</span>
              <span className="text-slate-400 dark:text-slate-500">•</span>
              <span className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors duration-200">Private & Secure</span>
              <span className="text-slate-400 dark:text-slate-500">•</span>
              <span className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors duration-200">Context-Aware</span>
            </div>
          </div>
        </div>
      </div>

      {isDemoOpen && (
        <>
          {/* Overlay with blur */}
          <div
            className="fixed inset-0 bg-black/70 dark:bg-black/70 backdrop-blur-md flex items-center justify-center z-50 animate-in fade-in-0 duration-300"
            onClick={() => setIsDemoOpen(false)}
          >
            {/* Modal content with pattern background */}
            <div
              className="relative bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-2 border-gray-200/50 dark:border-slate-600/50 rounded-2xl p-8 w-[70vw] h-[70vh] max-w-[70vw] max-h-[70vh] mx-4 shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Pattern background - subtle dot grid */}
              <div 
                className="absolute inset-0 opacity-[0.04] dark:opacity-[0.03]"
                style={{
                  backgroundImage: `radial-gradient(circle, currentColor 0.5px, transparent 0.5px)`,
                  backgroundSize: '32px 32px',
                  backgroundPosition: '0 0',
                }}
              />

              {/* Content wrapper */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Close button */}
                <button
                  onClick={() => setIsDemoOpen(false)}
                  className="absolute top-0 right-0 z-20 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Header */}
                <div className="mb-4 text-center flex-shrink-0">
                  <h2 className="text-gray-900 dark:text-white text-2xl font-semibold">Get Started with AIRA</h2>
                </div>

                {/* Iframe container */}
                <div className="flex-1 overflow-hidden flex items-center justify-center min-w-0 min-h-0 p-4">
                  <div className="w-full h-full flex items-center justify-center">
                    <div 
                      className="h-full w-auto" 
                      style={{ 
                        aspectRatio: '16 / 12',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto'
                      }}
                    >
                      <iframe 
                        src="https://scribehow.com/embed/Get_Started_with_AIRA__a40p3c9yRQG93pbZLE0uDw?skipIntro=true&as=video" 
                        width="100%" 
                        height="100%"
                        allow="fullscreen" 
                        style={{ border: 0, borderRadius: '0.5rem', display: 'block' }}
                        className="rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  )
}


